import { BrandIcon, PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { Button } from '@jotta/ui/Button'
import type { LocalizedCountry } from '@jotta/i18n'
import { useLocalizedCountryList } from '@jotta/i18n'
import type { ChangeEventHandler } from 'react'
import { useCallback } from 'react'
import {
  type FocusEventHandler,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react'
import { cn } from '@jotta/utils/css'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { getBrandZIndex } from '../../themes/zIndex'
import type { Country } from '@jotta/grpc-connect-openapi/country'
import {
  formatPhoneNumber,
  parsePhoneNumber,
} from '@jotta/utils/formatPhoneNumber'
import { FakeChangeEvent, FakeFocusEvent } from '../../utils/event'
import { t } from '@lingui/macro'
import SvgCaretDown from '@jotta/ui/SvgCaretDown'
import SvgFillXCircle from '@jotta/ui/SvgFillXCircle'
import SvgFillCheckCircle from '@jotta/ui/SvgFillCheckCircle'

export function validatePhoneNumber(phone: string | undefined) {
  return !phone || parsePhoneNumber(phone) ? undefined : t`Invalid phone number`
}

function resolveCountry(
  value: string | undefined,
  countryByCallingCode: Record<string, LocalizedCountry>,
) {
  if (value) {
    // try smart parse first
    const countryCallingCode = parsePhoneNumber(value)?.countryCallingCode
    const country =
      countryCallingCode && countryByCallingCode[countryCallingCode]
    if (country) {
      return country
    }

    // fall back to more robust checking of longest to shortest country calling code
    const prefixLength = value.startsWith('00')
      ? 2
      : value.startsWith('+')
        ? 1
        : Number.NaN
    for (
      let i = Math.min(prefixLength + 3, value.length);
      i > prefixLength;
      i--
    ) {
      const countryCallingCode = value.substring(prefixLength, i)
      const country = countryByCallingCode[countryCallingCode]
      if (country) {
        return country
      }
    }
  }
  return undefined
}

function resolveNationalNumber(
  value: string | undefined,
  countryCallingCode: string,
) {
  if (value?.startsWith(`00${countryCallingCode}`)) {
    return value.substring(2 + countryCallingCode.length)
  }
  if (value?.startsWith(`+${countryCallingCode}`)) {
    return value.substring(1 + countryCallingCode.length)
  }
  return value
}

function formatNational(normalizedPhoneNumber: string | undefined) {
  return parsePhoneNumber(normalizedPhoneNumber)
    ? formatPhoneNumber(normalizedPhoneNumber, 'NATIONAL')
    : undefined
}

function formatIDD(
  countryCallingCode: LocalizedCountry,
  nationalNumber: string,
) {
  if (nationalNumber) {
    const callingCode = countryCallingCode?.callingCode
    if (callingCode) {
      const inputValue = `00${callingCode}${nationalNumber}`
      const parsedValue = parsePhoneNumber(inputValue)
      return parsedValue
        ? `00${parsedValue.countryCallingCode}${parsedValue.phone}`
        : inputValue
    } else {
      return nationalNumber
    }
  } else {
    return ''
  }
}

function ChevronDown({ className }: { className?: string }) {
  return (
    <PlainBrandIcon className={className} icon={SvgCaretDown} height={16} />
  )
}

const zIndexModal = getBrandZIndex('modal')

function CountryCallingCodeSelector({
  countries,
  disabled,
  value,
  onSelectCountry,
  onCloseAutoFocus,
}: {
  countries: LocalizedCountry[]
  disabled: boolean | undefined
  value?: LocalizedCountry
  onSelectCountry: (c: LocalizedCountry) => void
  onCloseAutoFocus: () => void
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        type="button"
        className="btn btn-ghost h-10 bg-grey3"
        disabled={disabled}
      >
        {value && (
          <>
            <span className="text-2xl">{value.flag}</span>
            <span>{value.callingCode}</span>
          </>
        )}
        <ChevronDown />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={-40}
          onCloseAutoFocus={event => {
            event.preventDefault()
            onCloseAutoFocus()
          }}
          style={{
            zIndex: zIndexModal,
          }}
          className="flex max-h-80 w-auto flex-col overflow-y-auto bg-white font-medium shadow-[0_0_12px_0_#00000026] focus-visible:shadow-[0_0_12px_0_#00000026]"
        >
          <div className="relative flex h-10 flex-auto flex-shrink-0 flex-grow-0 flex-nowrap items-center gap-1 border-b border-gray-300 pl-3 pr-2">
            {value && (
              <>
                <div className="flex flex-auto items-center gap-2">
                  <span className="text-2xl">{value.flag}</span>
                  <span>{value.name}</span>
                </div>
                <span>+{value.callingCode}</span>
                <ChevronDown className="rotate-180" />
              </>
            )}
          </div>

          <div className="overflow-y-auto">
            {countries.map(c => {
              const { callingCode, flag, name } = c
              if (!callingCode) {
                return
              }

              return (
                <DropdownMenu.Item
                  key={c.code}
                  className="flex h-10 cursor-pointer flex-nowrap items-center gap-2 px-3 data-[highlighted]:bg-[#EBEBEB] focus-visible:shadow-none"
                  onSelect={() => onSelectCountry(c)}
                  textValue={name}
                >
                  <span className="text-2xl">{flag}</span>
                  <span>{name}</span>
                  <span className="flex-auto text-right">+{callingCode}</span>
                </DropdownMenu.Item>
              )
            })}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

// Takes and emits phone numbers on the IDD format (e.g. 004797702530) which is what the backend expects and delivers.
// Some additional cleverness:
//  - accepts leading '+' instead of '00' but will always emit the IDD format
//  - tries to format the visible value when appropriate (004797702530 becomes +47 97 70 25 30, 0017019976600 becomes +1 (701) 997-6600)
//  - emits and accepts partial/invalid values, tries to gracefully support changing the country calling code while inputting a partial and/or formatted national number😅
//  - inline display of verified numbers
export const PhoneInput = forwardRef(function PhoneInput(
  {
    className,
    country,
    placeholder,
    disabled,
    value,
    onBlur,
    onChange,
    phoneNumberVerified = false,
    name,
    autoComplete,
    autoFocus,
    ...props
  }: {
    className?: string
    country?: Country
    disabled?: boolean
    placeholder?: string
    phoneNumberVerified?: boolean
    name?: string
    value?: string
    onChange?: ChangeEventHandler<HTMLInputElement>
    onBlur?: FocusEventHandler<HTMLInputElement>
    autoComplete?: string
    autoFocus?: boolean
    'aria-errormessage'?: string
    'aria-invalid'?: boolean
  },
  ref,
) {
  const {
    countries,
    countryByCallingCode,
    countryByCountry,
    countryInfoByCode,
  } = useLocalizedCountryList()

  // Internal state. Try to format the national number part without messing too much with what the user writes.
  const localizedCountryParam = countryByCountry(country)
  const [localizedCountry, setLocalizedCountry] = useState(
    localizedCountryParam || countryInfoByCode['no'],
  )
  const [nationalNumber, setNationalNumberState] = useState('')
  const nationalNumberRef = useRef('')
  const [hiddenInput, setHiddenInput] = useState<HTMLInputElement | null>()
  const inputRef = useRef<HTMLInputElement>(null)

  const setNationalNumber = useCallback(
    (value: string) => {
      nationalNumberRef.current = value
      setNationalNumberState(value)
    },
    [setNationalNumberState],
  )

  // update internal state from props
  useEffect(() => {
    const trimmedValue = value?.trim()
    const newCountry =
      resolveCountry(trimmedValue, countryByCallingCode) ||
      localizedCountryParam

    const newCountryCallingCode = newCountry?.callingCode
    if (newCountryCallingCode) {
      setLocalizedCountry(current =>
        // do not overwrite countries sharing the same calling code
        current.callingCode === newCountryCallingCode ? current : newCountry,
      )

      if (
        // update internal state from value only if input element is not active
        document.activeElement !== inputRef.current
      ) {
        const newNationalNumber =
          formatNational(trimmedValue) ||
          resolveNationalNumber(trimmedValue, newCountryCallingCode) ||
          ''
        setNationalNumber(newNationalNumber)
      }
    }
  }, [
    value,
    localizedCountryParam,
    countryByCallingCode,
    setLocalizedCountry,
    setNationalNumber,
  ])

  // emit onChange when internal state changes
  // NB: we depend on the nationalNumber state to trigger the effect,
  // but use the nationalNumber ref to get the latest value to prevent looping effects
  useEffect(() => {
    if (!hiddenInput || !onChange) {
      return undefined
    }
    const normalizedValue = formatIDD(
      localizedCountry,
      nationalNumberRef.current,
    )
    if (hiddenInput.value !== normalizedValue) {
      hiddenInput.value = normalizedValue
      onChange(new FakeChangeEvent(hiddenInput))
    }
  }, [hiddenInput, onChange, localizedCountry, nationalNumber])

  return (
    <div className={cn('group flex gap-2', className)}>
      <input
        type="hidden"
        name={name}
        ref={input => {
          setHiddenInput(input)
          if (typeof ref === 'function') {
            ref(input)
          }
        }}
      />
      <CountryCallingCodeSelector
        countries={countries}
        value={localizedCountry}
        onSelectCountry={setLocalizedCountry}
        disabled={disabled}
        onCloseAutoFocus={() => {
          if (inputRef.current) {
            inputRef.current.focus()
          }
        }}
      />
      <div className="relative min-w-0 flex-auto">
        <input
          ref={inputRef}
          className="input h-10 w-full min-w-0 font-medium"
          inputMode="numeric"
          disabled={disabled}
          value={nationalNumber}
          placeholder={placeholder}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          aria-errormessage={props['aria-errormessage']}
          aria-invalid={props['aria-invalid']}
          onChange={event => setNationalNumber(event.target.value)}
          onBlur={() => {
            // format the internal state if the current value is recognized
            const formattedNationalNumber = formatNational(
              `00${localizedCountry.callingCode}${nationalNumber}`,
            )
            if (formattedNationalNumber) {
              setNationalNumber(formattedNationalNumber)
            }
            // emit onBlur event
            if (onBlur && hiddenInput) {
              onBlur(new FakeFocusEvent('blur', hiddenInput))
            }
          }}
        />
        {phoneNumberVerified && (
          <BrandIcon
            icon={SvgFillCheckCircle}
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2 text-good',
              {
                'transition group-hover:opacity-0': !disabled,
              },
            )}
          />
        )}
        <Button
          icon={SvgFillXCircle}
          type="button"
          tabIndex={-1}
          className={cn(
            'absolute right-2 top-1/2 hidden -translate-y-1/2 rounded',
            {
              'group-hover:block': !disabled && nationalNumber,
            },
          )}
          onClick={() => setNationalNumber('')}
        />
      </div>
    </div>
  )
})
